import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Navigate, Link } from "react-router-dom";
import { formatPoints } from "@/lib/format";
import { formatPesos } from "@/lib/currency";
import { flattenRefTree } from "@/lib/refTree";
import { toast } from "sonner";
import {
    ArrowUpRight, ArrowDownRight, Gift, Sparkles, Copy, Share2,
    Wallet as WalletIcon, ArrowDownToLine, Network,
} from "lucide-react";

export default function Wallet() {
    const { user, refresh } = useAuth();
    const { t } = useI18n();
    const [ledger, setLedger] = useState([]);
    const [preds, setPreds] = useState([]);
    const [withdrawals, setWithdrawals] = useState([]);
    const [refSummary, setRefSummary] = useState(null);

    const reloadAll = async () => {
        const [l, p, w, r] = await Promise.all([
            api.get("/wallet/ledger"),
            api.get("/wallet/predictions"),
            api.get("/withdrawals/me"),
            api.get("/referrals/me"),
        ]);
        setLedger(l.data);
        setPreds(p.data);
        setWithdrawals(w.data);
        setRefSummary(r.data);
    };

    useEffect(() => {
        if (user && user !== false) reloadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    if (user === false) return <Navigate to="/login" replace />;
    if (!user) return null;

    const refLink = user.referral_code
        ? `${window.location.origin}/registro?ref=${user.referral_code}`
        : "";

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="wallet-page">
            <h1 className="font-sora font-extrabold text-4xl sm:text-5xl tracking-tighter">{t("wallet.title")}</h1>

            <div className="mt-8 grid lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 relative overflow-hidden rounded-2xl border border-p2w-line bg-gradient-to-br from-p2w-surface via-p2w-surface to-p2w-elevated p-6 shadow-glow-violet">
                    <div className="glow-orb bg-p2w-green w-[220px] h-[220px] -bottom-12 -right-12 opacity-25" />
                    <div className="relative">
                        <div className="flex items-center justify-between">
                            <div className="text-xs uppercase tracking-widest text-p2w-gray flex items-center gap-2">
                                <WalletIcon className="w-3.5 h-3.5" /> {t("wallet.balance")}
                            </div>
                            <div className="flex items-center gap-2">
                                <Link to="/wallet/recargar" data-testid="btn-depositar">
                                    <Button size="sm" className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 hover:shadow-glow-green font-semibold h-8 px-3">
                                        Depositar
                                    </Button>
                                </Link>
                                <Link to="/wallet/retirar" data-testid="btn-retirar">
                                    <Button size="sm" variant="outline" className="border-p2w-line bg-transparent text-white hover:bg-p2w-elevated hover:text-white h-8 px-3">
                                        Retirar
                                    </Button>
                                </Link>
                            </div>
                        </div>
                        <div className="mt-2 font-sora font-extrabold text-6xl text-p2w-green number-tabular" data-testid="wallet-balance">
                            {formatPoints(user.points_balance)}
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-sm">
                            <span className="text-p2w-gray">pts</span>
                            <span className="px-2 py-0.5 rounded-md bg-p2w-elevated border border-p2w-line text-p2w-green font-sora font-bold text-xs number-tabular" data-testid="wallet-pesos">
                                = {formatPesos(user.points_balance)} pesos
                            </span>
                        </div>
                        <div className="mt-3 text-[10px] uppercase tracking-widest text-p2w-gray">
                            1 punto = 1 peso · equivalencia 1:1
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-6">
                    <div className="text-xs uppercase tracking-widest text-p2w-gray flex items-center gap-2">
                        <Network className="w-3.5 h-3.5" /> Tu código de referido
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                        <code data-testid="my-referral-code" className="flex-1 px-3 py-2 rounded-lg bg-p2w-elevated border border-p2w-line text-p2w-green font-mono text-xl font-bold tracking-widest text-center">
                            {user.referral_code || "—"}
                        </code>
                        <button
                            data-testid="copy-ref-code"
                            onClick={() => {
                                navigator.clipboard?.writeText(user.referral_code || "");
                                toast.success("Código copiado");
                            }}
                            className="p-2.5 rounded-lg bg-p2w-elevated border border-p2w-line text-p2w-gray hover:text-p2w-green hover:border-p2w-green/40 transition"
                        >
                            <Copy className="w-4 h-4" />
                        </button>
                    </div>
                    <button
                        data-testid="copy-ref-link"
                        onClick={() => {
                            navigator.clipboard?.writeText(refLink);
                            toast.success("Enlace copiado");
                        }}
                        className="mt-3 w-full text-xs text-p2w-gray hover:text-p2w-green flex items-center justify-center gap-2 py-2"
                    >
                        <Share2 className="w-3.5 h-3.5" /> Compartir enlace
                    </button>
                </div>
            </div>

            <Tabs defaultValue="preds" className="mt-10">
                <TabsList className="bg-p2w-elevated border border-p2w-line flex-wrap h-auto">
                    <TabsTrigger value="preds" data-testid="tab-preds" className="data-[state=active]:bg-p2w-green data-[state=active]:text-p2w-green-fg">
                        {t("wallet.my_preds")}
                    </TabsTrigger>
                    <TabsTrigger value="ledger" data-testid="tab-ledger" className="data-[state=active]:bg-p2w-green data-[state=active]:text-p2w-green-fg">
                        {t("wallet.history")}
                    </TabsTrigger>
                    <TabsTrigger value="withdrawals" data-testid="tab-withdrawals" className="data-[state=active]:bg-p2w-green data-[state=active]:text-p2w-green-fg">
                        Retiros
                    </TabsTrigger>
                    <TabsTrigger value="referrals" data-testid="tab-referrals" className="data-[state=active]:bg-p2w-green data-[state=active]:text-p2w-green-fg">
                        Referidos
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="preds" className="mt-6">
                    {preds.length === 0 ? <Empty msg={t("wallet.empty")} /> : (
                        <div className="overflow-hidden rounded-2xl border border-p2w-line bg-p2w-surface">
                            <table className="w-full text-sm">
                                <thead className="bg-p2w-elevated border-b border-p2w-line text-p2w-gray text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-4 py-3">{t("wallet.market")}</th>
                                        <th className="text-left px-4 py-3">{t("wallet.option")}</th>
                                        <th className="text-right px-4 py-3">{t("wallet.stake")}</th>
                                        <th className="text-right px-4 py-3">{t("wallet.payout")}</th>
                                        <th className="text-right px-4 py-3">{t("wallet.status")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {preds.map((p) => (
                                        <tr key={p.id} className="border-t border-p2w-line">
                                            <td className="px-4 py-3">
                                                <Link to={`/mercado/${p.market_id}`} className="text-white hover:text-p2w-green">{p.market_title}</Link>
                                            </td>
                                            <td className="px-4 py-3 text-p2w-gray">{p.option_label}</td>
                                            <td className="px-4 py-3 text-right number-tabular text-white">{formatPoints(p.stake_points)}</td>
                                            <td className="px-4 py-3 text-right number-tabular">
                                                <span className={p.payout > 0 ? "text-p2w-green" : "text-p2w-gray"}>
                                                    {p.payout > 0 ? `+${formatPoints(p.payout)}` : "—"}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right"><StatusBadge status={p.status} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="ledger" className="mt-6">
                    {ledger.length === 0 ? <Empty msg={t("wallet.empty")} /> : (
                        <div className="space-y-2">
                            {ledger.map((e) => (
                                <div key={e.id} className="flex items-center justify-between px-5 py-4 rounded-xl bg-p2w-surface border border-p2w-line">
                                    <div className="flex items-center gap-3">
                                        <LedgerIcon type={e.type} amount={e.amount} />
                                        <div>
                                            <div className="text-sm text-white capitalize font-medium">{ledgerLabel(e.type)}</div>
                                            <div className="text-xs text-p2w-gray">{new Date(e.created_at).toLocaleString()}</div>
                                        </div>
                                    </div>
                                    <div className={`font-sora font-bold text-lg number-tabular ${e.amount > 0 ? "text-p2w-green" : e.amount < 0 ? "text-p2w-red" : "text-p2w-gray"}`}>
                                        {e.amount > 0 ? "+" : ""}{formatPoints(e.amount)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="withdrawals" className="mt-6">
                    {withdrawals.length === 0 ? (
                        <Empty msg="Aún no has solicitado retiros." />
                    ) : (
                        <div className="space-y-2">
                            {withdrawals.map((w) => (
                                <div key={w.id} className="px-5 py-4 rounded-xl bg-p2w-surface border border-p2w-line">
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="flex-1 min-w-0">
                                            <div className="font-sora font-bold text-p2w-green text-xl number-tabular">{formatPoints(w.amount)} pts</div>
                                            {w.net_amount !== undefined && w.net_amount !== w.amount && (
                                                <div className="text-xs text-p2w-gray mt-1">
                                                    Neto recibido: <span className="text-white font-semibold">{formatPoints(w.net_amount)} pts</span>
                                                    {w.withholding > 0 && <span className="text-p2w-amber"> · Retención {formatPoints(w.withholding)}</span>}
                                                </div>
                                            )}
                                            <div className="text-xs text-p2w-gray mt-1 break-all">{w.destination}</div>
                                            <div className="text-[10px] text-p2w-gray mt-1">Solicitado: {new Date(w.requested_at).toLocaleString()}</div>
                                            {w.paid_at && (
                                                <div className="text-[10px] text-p2w-green mt-0.5">Pagado: {new Date(w.paid_at).toLocaleString()}</div>
                                            )}
                                        </div>
                                        <StatusBadge status={w.status} />
                                    </div>
                                    {w.status === "paid" && w.payment_proof_url && (
                                        <div className="mt-3 pt-3 border-t border-p2w-line">
                                            <div className="text-[10px] uppercase tracking-widest text-p2w-gray mb-2">Comprobante de envío</div>
                                            {w.payment_proof_url.startsWith("data:image") || /\.(png|jpe?g|webp|gif)$/i.test(w.payment_proof_url) ? (
                                                <a href={w.payment_proof_url} target="_blank" rel="noreferrer" data-testid={`user-proof-${w.id}`}>
                                                    <img src={w.payment_proof_url} alt="Comprobante" className="max-h-48 w-auto rounded-lg border border-p2w-line bg-p2w-bg" />
                                                </a>
                                            ) : (
                                                <a href={w.payment_proof_url} target="_blank" rel="noreferrer" data-testid={`user-proof-${w.id}`} className="inline-flex items-center gap-2 text-sm text-p2w-blue underline hover:text-p2w-green">
                                                    Ver comprobante (PDF)
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="referrals" className="mt-6">
                    {refSummary && <ReferralsCard summary={refSummary} />}
                </TabsContent>
            </Tabs>
        </div>
    );
}

function WithdrawDialog({ balance, onDone }) {
    const [open, setOpen] = useState(false);
    const [amount, setAmount] = useState(0);
    const [destination, setDestination] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        const n = Number(amount);
        if (n <= 0) return toast.error("Cantidad inválida");
        if (n > balance) return toast.error("Saldo insuficiente");
        if (!destination.trim()) return toast.error("Indica un destino");
        setSubmitting(true);
        try {
            await api.post("/withdrawals", { amount: n, destination: destination.trim(), notes });
            toast.success("Solicitud enviada al admin");
            setOpen(false);
            setAmount(0); setDestination(""); setNotes("");
            onDone?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button size="sm" data-testid="request-withdrawal-btn" className="bg-p2w-elevated text-white hover:bg-p2w-line border border-p2w-line gap-2">
                    <ArrowDownToLine className="w-3.5 h-3.5" /> Retirar
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-p2w-surface border-p2w-line text-white">
                <DialogHeader>
                    <DialogTitle className="font-sora">Solicitar retiro</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="px-4 py-3 rounded-lg bg-p2w-elevated border border-p2w-line flex items-center justify-between text-sm">
                        <span className="text-p2w-gray">Disponible</span>
                        <span className="font-sora font-bold text-p2w-green number-tabular text-lg">{formatPoints(balance)} pts</span>
                    </div>
                    <div>
                        <Label className="text-p2w-gray text-xs uppercase tracking-widest">Cantidad a retirar</Label>
                        <Input
                            type="number" min={1} max={balance}
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            data-testid="withdraw-amount-input"
                            className="mt-2 bg-p2w-elevated border-p2w-line text-white text-xl font-sora font-bold focus-visible:ring-p2w-green/40"
                        />
                        <div className="mt-2 flex gap-2 flex-wrap">
                            {[500, 1000, 2500, "max"].map((v) => (
                                <button key={v} onClick={() => setAmount(v === "max" ? balance : v)} className="text-xs px-3 py-1 rounded-md border border-p2w-line bg-p2w-elevated text-p2w-gray hover:text-p2w-green hover:border-p2w-green/40">
                                    {v === "max" ? "MAX" : v}
                                </button>
                            ))}
                        </div>
                        {Number(amount) > 0 && (
                            <div data-testid="withdraw-pesos-preview" className="mt-2 text-xs text-p2w-green flex items-center gap-2">
                                <span>Recibirás aproximadamente</span>
                                <span className="font-sora font-bold text-base number-tabular">{formatPesos(amount)} pesos</span>
                            </div>
                        )}
                    </div>
                    <div>
                        <Label className="text-p2w-gray text-xs uppercase tracking-widest">Destino (cuenta / wallet)</Label>
                        <Input
                            value={destination}
                            onChange={(e) => setDestination(e.target.value)}
                            data-testid="withdraw-destination-input"
                            placeholder="Ej. ES12 1234 5678 …"
                            className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                        />
                    </div>
                    <div>
                        <Label className="text-p2w-gray text-xs uppercase tracking-widest">Notas (opcional)</Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40"
                        />
                    </div>
                    <p className="text-xs text-p2w-gray">El admin revisará la solicitud. Los puntos quedan retenidos hasta la aprobación.</p>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} className="border-p2w-line bg-p2w-elevated text-white hover:bg-p2w-line hover:text-white">Cancelar</Button>
                    <Button onClick={submit} disabled={submitting} data-testid="confirm-withdrawal" className="bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold">
                        Enviar solicitud
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

const LEVEL_STYLES = [
    "border-p2w-green/40 bg-p2w-green/5",
    "border-p2w-purple/40 bg-p2w-purple/5",
    "border-p2w-magenta/40 bg-p2w-magenta/5",
];

function ReferralsCard({ summary }) {
    const rows = flattenRefTree(summary.tree);
    return (
        <div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <Stat label="Nivel 1" value={summary.totals.l1} sub={`+${summary.bonuses.l1} pts c/u`} color="text-p2w-green" />
                <Stat label="Nivel 2" value={summary.totals.l2} sub={`+${summary.bonuses.l2} pts c/u`} color="text-p2w-purple" />
                <Stat label="Nivel 3" value={summary.totals.l3} sub={`+${summary.bonuses.l3} pts c/u`} color="text-p2w-magenta" />
                <Stat label="Ganado" value={formatPoints(summary.earnings)} sub="puntos totales" color="text-p2w-green" big />
            </div>
            {rows.length === 0 ? (
                <div className="rounded-2xl border border-p2w-line bg-p2w-surface p-10 text-center">
                    <Network className="w-10 h-10 text-p2w-gray mx-auto mb-3" />
                    <p className="text-p2w-gray">Aún no tienes referidos. Comparte tu código y gana puntos hasta 3 niveles abajo.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {rows.map((r) => (
                        <div
                            key={r.id}
                            className={`rounded-xl border ${LEVEL_STYLES[r.level - 1]} p-3 flex items-center justify-between`}
                            style={{ marginLeft: (r.level - 1) * 20 }}
                        >
                            <div className="flex items-center gap-2">
                                <Badge className="bg-p2w-elevated border-p2w-line text-p2w-gray text-[10px]">L{r.level}</Badge>
                                <span className="text-sm font-medium text-white">@{r.username}</span>
                            </div>
                            <span className="text-xs font-sora font-bold text-p2w-green number-tabular">{formatPoints(r.points_balance)} pts</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, sub, color, big }) {
    return (
        <div className="rounded-xl border border-p2w-line bg-p2w-surface p-4">
            <div className="text-[10px] uppercase tracking-widest text-p2w-gray">{label}</div>
            <div className={`mt-1 font-sora font-extrabold ${big ? "text-2xl" : "text-3xl"} number-tabular ${color || "text-white"}`}>{value}</div>
            {sub && <div className="mt-0.5 text-[10px] text-p2w-gray">{sub}</div>}
        </div>
    );
}

function Empty({ msg }) {
    return (
        <div className="text-center py-16 rounded-2xl border border-p2w-line bg-p2w-surface">
            <p className="text-p2w-gray">{msg}</p>
            <Link to="/mercados" className="inline-block mt-4 text-p2w-green font-semibold hover:underline">Explorar mercados →</Link>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        won: { color: "bg-p2w-green/15 text-p2w-green border-p2w-green/40", label: "Ganado" },
        lost: { color: "bg-p2w-red/15 text-p2w-red border-p2w-red/40", label: "Perdido" },
        active: { color: "bg-p2w-blue/15 text-p2w-blue border-p2w-blue/40", label: "Activo" },
        refunded: { color: "bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40", label: "Reembolsado" },
        pending: { color: "bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40", label: "Pendiente" },
        on_hold: { color: "bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40", label: "En revisión" },
        approved: { color: "bg-p2w-green/15 text-p2w-green border-p2w-green/40", label: "Aprobado" },
        paid: { color: "bg-p2w-blue/15 text-p2w-blue border-p2w-blue/40", label: "Pagado" },
        rejected: { color: "bg-p2w-red/15 text-p2w-red border-p2w-red/40", label: "Rechazado" },
    };
    const cfg = map[status] || map.active;
    return <Badge className={`${cfg.color} text-[10px] uppercase tracking-wider border`}>{cfg.label}</Badge>;
}

function LedgerIcon({ type, amount }) {
    if (type === "welcome" || type === "referral_bonus") return <Gift className="w-5 h-5 text-p2w-purple" />;
    if (type === "win" || type === "market_payout" || type === "creator_fee") return <Sparkles className="w-5 h-5 text-p2w-green" />;
    if (type === "admin_adjust") return <WalletIcon className="w-5 h-5 text-p2w-blue" />;
    if (type === "withdrawal_hold" || type === "withdrawal_approved") return <ArrowDownToLine className="w-5 h-5 text-p2w-amber" />;
    return amount >= 0 ? <ArrowUpRight className="w-5 h-5 text-p2w-green" /> : <ArrowDownRight className="w-5 h-5 text-p2w-red" />;
}

function ledgerLabel(type) {
    const map = {
        welcome: "Bonus de bienvenida",
        stake: "Predicción",
        win: "Premio",
        market_payout: "Premio de mercado",
        creator_fee: "Comisión creador",
        refund: "Reembolso",
        referral_bonus: "Bonus referido",
        admin_adjust: "Ajuste admin",
        withdrawal_hold: "Retiro solicitado",
        withdrawal_approved: "Retiro aprobado",
        withdrawal_refund: "Retiro rechazado",
    };
    return map[type] || type;
}
